import { Position, Subscription, TrackingData } from "../types";

// Re-export TrackingData for backwards compatibility
export type { TrackingData } from "../types";

export interface TrackingProvider {
  readonly id: string;
  readonly name: string;
  readonly type: "device" | "simulation" | "bluetooth";

  isAvailable(): Promise<boolean>;
  isActive(): boolean;
  getCurrentData(): TrackingData;

  start(): Promise<void>;
  stop(): void;
  pause?(): void;
  resume?(): void;

  subscribe(callback: (data: TrackingData) => void): Subscription;

  // Optional configuration
  configure?(config: any): void;
  getConfiguration?(): any;

  destroy(): void;
}

export interface TrackingProviderCapabilities {
  hasPosition: boolean;
  hasHeading: boolean;
  hasSpeed: boolean;
  canPause: boolean;
  canConfigure: boolean;
  isRealtime: boolean;
}

export abstract class BaseTrackingProvider implements TrackingProvider {
  abstract readonly id: string;
  abstract readonly name: string;
  abstract readonly type: "device" | "simulation" | "bluetooth";

  protected subscribers = new Set<(data: TrackingData) => void>();
  protected _isActive = false;
  protected currentData: TrackingData;

  constructor() {
    this.currentData = {
      position: null,
      isActive: false,
      source: "unknown",
    };
  }

  abstract isAvailable(): Promise<boolean>;

  isActive(): boolean {
    return this._isActive;
  }

  getCurrentData(): TrackingData {
    return { ...this.currentData };
  }

  abstract start(): Promise<void>;
  abstract stop(): void;

  subscribe(callback: (data: TrackingData) => void): Subscription {
    this.subscribers.add(callback);

    // Immediately call with current data
    callback(this.getCurrentData());

    return {
      unsubscribe: () => {
        this.subscribers.delete(callback);
      },
    };
  }

  protected notifySubscribers(data: TrackingData): void {
    this.currentData = { ...data };
    this.subscribers.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in tracking provider subscriber:`, error);
      }
    });
  }

  destroy(): void {
    this.stop();
    this.subscribers.clear();
  }
}
